import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import { useMutation, useApolloClient } from '@apollo/client';
import { LOGOUT } from '../graphql/logout';
import { useAuthenticated } from '../hooks/useAuthenticated';
import { useNavigateHome } from '../hooks/useNavigateHome';
import { useActiveFinalExam } from '../hooks/useActiveFinalExam';
import { NavBarStyles } from '../styles/NavBarStyles';
import CreateFinalExamModal from './CreateFinalExamModal';
import { useState, useEffect } from 'react';

const NavBar = () => {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const avatarBoxRef = React.useRef<HTMLDivElement>(null);
  const [isCreateFinalExamModalOpen, setIsCreateFinalExamModalOpen] =
    useState(false);
  const { isAuthenticated, isAdmin, loading, userName, isPlanActive } =
    useAuthenticated();
  const navigate = useNavigate();
  const { navigateHome } = useNavigateHome();
  const client = useApolloClient();
  const { hasActiveFinalExam, activeFinalExam } = useActiveFinalExam();

  // Get the first initial of the user's name
  const userInitial = userName ? userName.charAt(0).toUpperCase() : 'U';

  const [logout] = useMutation(LOGOUT, {
    onCompleted: async () => {
      await client.clearStore();
    },
    update: (cache) => {
      cache.evict({ fieldName: 'getMe' });
      cache.gc();
    },
  });

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (Boolean(anchorElUser)) {
      setAnchorElUser(null);
    } else {
      setAnchorElUser(avatarBoxRef.current);
    }
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    handleCloseUserMenu();
    await logout();
    navigate('/login');
  };

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        avatarBoxRef.current &&
        !avatarBoxRef.current.contains(event.target as Node)
      ) {
        handleCloseUserMenu();
      }
    };

    if (Boolean(anchorElUser)) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorElUser]);

  if (loading || !isAuthenticated) {
    return null;
  }

  return (
    <AppBar sx={NavBarStyles.container} position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box
            component="img"
            src="https://res.cloudinary.com/dh0eho4vx/image/upload/v1752986194/yin_yang_mpsxtn.png"
            alt="Yin Yang"
            sx={{
              ...NavBarStyles.icon,
              cursor: 'pointer',
              width: 40,
              height: 40,
            }}
            onClick={navigateHome}
          />
          <Typography
            variant="h6"
            noWrap
            component="a"
            sx={NavBarStyles.logo}
            onClick={navigateHome}
          >
            Prep Qi
          </Typography>
          <Box sx={NavBarStyles.pages}>
            <Button
              key="home"
              onClick={navigateHome}
              sx={NavBarStyles.menuItem}
            >
              Home
            </Button>
            {isAdmin ? (
              <>
                <Button
                  key="question-bank"
                  onClick={() => navigate('/admin/question-bank')}
                  sx={NavBarStyles.menuItem}
                >
                  Question Bank
                </Button>
                <Button
                  key="manage-users"
                  onClick={() => navigate('/admin/users')}
                  sx={NavBarStyles.menuItem}
                >
                  Manage Users
                </Button>
                <Button
                  key="create"
                  onClick={() => navigate('/admin/questions/new')}
                  sx={NavBarStyles.menuItem}
                >
                  Create
                </Button>
              </>
            ) : (
              <>
                {isPlanActive && (
                  <>
                    <Button
                      key="history"
                      onClick={() => navigate('/completed-rounds')}
                      sx={NavBarStyles.menuItem}
                    >
                      My Rounds
                    </Button>
                    <Button
                      key="my-exams"
                      onClick={() => navigate('/my-exams')}
                      sx={NavBarStyles.menuItem}
                    >
                      My Exams
                    </Button>
                    <Button
                      key="my-bookmarks"
                      onClick={() => navigate('/my-bookmarks')}
                      sx={NavBarStyles.menuItem}
                    >
                      My Bookmarks
                    </Button>
                    <Button
                      key="contact"
                      onClick={() => navigate('/contact')}
                      sx={NavBarStyles.menuItem}
                    >
                      Contact us
                    </Button>
                    <Button
                      key="faq"
                      onClick={() => navigate('/faq')}
                      sx={NavBarStyles.menuItem}
                    >
                      FAQ & Return Policy
                    </Button>
                    <Button
                      key="take-final-exam"
                      onClick={() =>
                        hasActiveFinalExam
                          ? navigate(`/final-exam/${activeFinalExam?.id}`)
                          : setIsCreateFinalExamModalOpen(true)
                      }
                      sx={{
                        ...NavBarStyles.menuItem,
                        background: 'linear-gradient(45deg, #ff4da6, #4682b4)',
                        color: 'white',
                        px: 3,
                        py: 1,
                        borderRadius: '25px',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        letterSpacing: '0.5px',
                        boxShadow:
                          '0 6px 20px rgba(255, 102, 178, 0.4), 0 4px 15px rgba(70, 130, 180, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #ff66b2, #87ceeb)',
                          transform: 'translateY(-3px) scale(1.05)',
                          boxShadow:
                            '0 12px 30px rgba(255, 102, 178, 0.6), 0 8px 25px rgba(70, 130, 180, 0.4)',
                          border: '2px solid rgba(255, 255, 255, 0.5)',
                        },
                        '&:active': {
                          transform: 'translateY(-1px) scale(1.02)',
                        },
                        '&:after': {
                          display: 'none',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background:
                            'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                          transition: 'left 0.5s',
                        },
                        '&:hover::before': {
                          left: '100%',
                        },
                      }}
                    >
                      {hasActiveFinalExam
                        ? '🔄 Resume Final Exam ✨'
                        : '🎯 Take Final Exam ✨'}
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
          <Box sx={{ flexGrow: 0, position: 'relative' }} ref={avatarBoxRef}>
            <IconButton
              onClick={handleOpenUserMenu}
              sx={{ p: 0, marginLeft: '60px' }}
            >
              <Avatar sx={{ ...NavBarStyles.avatar }}>{userInitial}</Avatar>
            </IconButton>
            {Boolean(anchorElUser) && (
              <Box sx={NavBarStyles.customDropdown}>
                {!isAdmin && (
                  <Box
                    sx={NavBarStyles.customMenuItem}
                    onClick={() => {
                      handleCloseUserMenu();
                      navigate('/profile');
                    }}
                  >
                    <Typography sx={NavBarStyles.menuItemText}>
                      Profile
                    </Typography>
                  </Box>
                )}
                <Box sx={NavBarStyles.customMenuItem} onClick={handleLogout}>
                  <Typography sx={NavBarStyles.menuItemText}>Logout</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Create Final Exam Modal */}
      <CreateFinalExamModal
        open={isCreateFinalExamModalOpen}
        onClose={() => setIsCreateFinalExamModalOpen(false)}
      />
    </AppBar>
  );
};

export default NavBar;
